/**
 * Progression helpers — pure, FE-only.
 * Used by the intra-workout UI to derive "last session" reference, PR,
 * and lightweight progression suggestions.
 *
 * No backend calls. No side effects. Numbers only.
 */

/**
 * Pick the best set from a list of logged sets.
 * Heaviest weight wins; tie-break on reps.
 *
 * @param {Array<{weight:number, reps:number, rpe?:number}>} sets
 * @returns {?{weight:number, reps:number, rpe:?number}}
 */
export const bestSetOf = (sets) => {
  if (!Array.isArray(sets) || sets.length === 0) return null;
  let best = null;
  sets.forEach((s, i) => {
    if (!s) return;
    const w = Number(s.weight) || 0;
    const r = Number(s.reps) || 0;
    if (!best || w > best.weight || (w === best.weight && r > best.reps)) {
      best = { weight: w, reps: r, rpe: s.rpe ?? null, setNumber: i + 1 };
    }
  });
  return best;
};

/**
 * Derive two gentle progression options from a "last session" best set.
 *
 * Option A — stay on the same weight, add 1 rep.
 * Option B — step up one weight increment, cap reps below previous top reps.
 *
 * Both options are intentionally modest. The UI should render them as an
 * _optional_ reference, never as a prescription.
 *
 * @param {{weight:number, reps:number}} bestSet - reference best set
 * @param {number} [weightStep=2.5] - kg increment for Option B
 * @returns {?[{weight:number, reps:number, label:string}, {weight:number, reps:number, label:string}]}
 */
export const buildSuggestions = (bestSet, weightStep = 2.5) => {
  if (!bestSet || !bestSet.weight || !bestSet.reps) return null;
  const { weight, reps } = bestSet;

  // Option A — same weight, +1 rep
  const optA = { weight, reps: reps + 1, label: 'Opción 1' };

  // Option B — step up weight, cap reps
  const nextWeight = Math.round((weight + weightStep) * 2) / 2; // clamp to 0.5 kg
  const cappedReps = Math.max(6, reps - 2); // never below 6
  const optB = { weight: nextWeight, reps: cappedReps, label: 'Opción 2' };

  return [optA, optB];
};

/**
 * Adaptive suggestions — adjust aggressiveness based on today's performance
 * vs the last session.
 *
 *   mode = 'aggressive'   → push: same w + 2 reps OR +step kg with reps - 1
 *   mode = 'neutral'      → buildSuggestions default
 *   mode = 'conservative' → hold: same w same reps OR -1 rep, no weight step
 *
 * @param {{weight:number, reps:number}} bestSet
 * @param {'aggressive'|'neutral'|'conservative'} mode
 * @param {number} weightStep
 */
export const buildSuggestionsAdaptive = (bestSet, mode = 'neutral', weightStep = 2.5) => {
  if (!bestSet || !bestSet.weight || !bestSet.reps) return null;
  const { weight, reps } = bestSet;
  const next = Math.round((weight + weightStep) * 2) / 2;

  if (mode === 'aggressive') {
    return [
      { weight, reps: reps + 2, label: 'Opción 1' },
      { weight: next, reps: Math.max(6, reps - 1), label: 'Opción 2' },
    ];
  }
  if (mode === 'conservative') {
    return [
      { weight, reps, label: 'Opción 1' },
      { weight, reps: Math.max(1, reps - 1), label: 'Opción 2' },
    ];
  }
  // neutral — original
  return buildSuggestions(bestSet, weightStep);
};

/**
 * Simple day-relative date label (Spanish). Preserves behavior of
 * LastSessionsHint.formatRelativeDate so the new cards feel consistent.
 */
export const formatRelativeDateEs = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T12:00:00');
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const diffDays = Math.round((today - d) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'hoy';
    if (diffDays === 1) return 'ayer';
    if (diffDays < 7) return `hace ${diffDays} días`;
    if (diffDays < 30) {
      const w = Math.round(diffDays / 7);
      return `hace ${w} ${w === 1 ? 'semana' : 'semanas'}`;
    }
    if (diffDays < 365) {
      const m = Math.round(diffDays / 30);
      return `hace ${m} ${m === 1 ? 'mes' : 'meses'}`;
    }
    const y = Math.round(diffDays / 365);
    return `hace ${y} ${y === 1 ? 'año' : 'años'}`;
  } catch { return ''; }
};

/**
 * Short date chip for summary cards, e.g. "17 mar".
 */
export const formatShortDateEs = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T12:00:00');
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }).replace('.', '');
  } catch { return ''; }
};

/**
 * Resolve per-set targets with priority `target_set_plan[i] > scalar`.
 *
 * Per-set planning is the authoritative primitive. Scalar `target_*` fields
 * are the fallback / simple mode.
 *
 * Used by the active workout interface to compute placeholders for the
 * current set's reps/weight/rest_time inputs, and by any view that needs to
 * know "what's the plan for set i?".
 *
 * @param {Object} exercise - WorkoutSet shape
 * @param {number} setIdx - 0-based index of the set whose plan we want
 * @returns {{ reps: ?number, weight: ?number, rest_time: ?number, time: ?number }}
 */
export const resolveSetTargets = (exercise, setIdx) => {
  if (!exercise) return { reps: null, weight: null, rest_time: null, time: null };
  const plan = Array.isArray(exercise.target_set_plan) ? exercise.target_set_plan[setIdx] : null;
  return {
    reps:      plan?.reps      ?? exercise.target_reps      ?? exercise.reps      ?? null,
    weight:    plan?.weight    ?? exercise.target_weight    ?? exercise.weight    ?? null,
    rest_time: plan?.rest_time ?? exercise.rest_time        ?? null,
    time:      plan?.time      ?? null, // cardio
  };
};

/**
 * Best RPE of a logged set list (for the "RPE 9" meta line in summaries).
 */
export const maxRpeOf = (sets) => {
  if (!Array.isArray(sets) || sets.length === 0) return null;
  const vals = sets.map((s) => s && s.rpe).filter((v) => v != null);
  if (vals.length === 0) return null;
  return Math.max(...vals);
};
